import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormRadioGroup from 'components/KNForm/KNFormRadioGroup'
import KNFormText from 'components/KNForm/KNFormText'
import { CompanyType } from 'context/authentication/Company.types'
import { Role } from 'context/authentication/Role.types'
import { getSelectedCompany } from 'context/authentication/User.helpers'
import { UserContext } from 'context/authentication/UserContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { isCompanyType } from 'global/helpers/authorization'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import type { WithId } from 'global/helpers/requests'
import { regexEmail } from 'global/helpers/validators'
import { type ReactElement, useContext, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createUser, editUser, USERS_QUERY_KEY } from './UserManager.service'
import type { CreateOrEditUserFormValues, UserData } from './UserManager.types'

interface CreateOrEditUserDialogPayload {
  readonly user: UserData
}

interface CreateOrEditUserDialogProps {
  readonly payload?: CreateOrEditUserDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const CreateOrEditUserDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: CreateOrEditUserDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<CreateOrEditUserFormValues>()
  const queryClient = useQueryClient()
  const createUserMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })
  const updateUserMutation = useMutation({
    mutationFn: (data: WithId<CreateOrEditUserFormValues>) => editUser(data.id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })
  const { user } = useContext(UserContext)
  const isSelf = payload?.user.email === user?.email
  const selectedCompany = getSelectedCompany()
  const isCompanyLevel = selectedCompany?.type !== CompanyType.Operator

  useEffect(() => {
    if (!open) {
      return
    }
    if (selectedCompany && isCompanyLevel) {
      reset(
        processDefaultValues({
          email: payload?.user.email,
          displayName: payload?.user.displayName,
          companyCid: selectedCompany.cid,
          role: payload?.user.roles[selectedCompany.cid],
        }),
      )
    } else {
      reset(
        processDefaultValues({
          email: payload?.user.email,
          displayName: payload?.user.displayName,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<CreateOrEditUserFormValues> = async (data: CreateOrEditUserFormValues) =>
    handleFormSubmission(async () => {
      if (payload) {
        await updateUserMutation.mutateAsync({ id: payload.user.cid, ...data })
      } else {
        await createUserMutation.mutateAsync(data)
      }
    }, {
      onSuccess: () => {
        if (!isCompanyType(CompanyType.Operator)) {
          analyticsEvent(payload ? 'polestar_user_updated' : 'polestar_user_created')
        }
        onAction()
      },
      onError: (error) => {
        setError('root', processServerErrorMessages(error))
      },
    })

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={
        payload
          ? `${payload.user.displayName} — ${t('screens.shared.user_manager.add_edit_user.edit_details')}`
          : t('screens.shared.user_manager.add_edit_user.new_user')
      }
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {payload
            ? t('screens.shared.user_manager.add_edit_user.edit_details')
            : t('screens.shared.user_manager.add_edit_user.new_user')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack spacing={2}>
        <KNFormText
          name="email"
          label={t('screens.shared.user_manager.add_edit_user.email')}
          control={control}
          rules={{
            pattern: {
              value: regexEmail,
              message: t('form.validation.invalid_email'),
            },
            required: t('form.validation.required'),
          }}
          disabled={!!payload}
        />
        <KNFormText
          name="displayName"
          label={t('screens.shared.user_manager.add_edit_user.display_name')}
          control={control}
          rules={{
            required: t('form.validation.required'),
            validate: (value) => !!value?.trim() || t('form.validation.required'),
          }}
        />
        {isCompanyLevel && !isSelf && (
          <KNFormRadioGroup
            name="role"
            label={t('screens.shared.user_manager.grant_role.role')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
            options={Object.keys(Role)
              .map((role) => ({
                value: role,
                label: t(`screens.shared.user_manager.roles.${role}`),
              }))}
          />
        )}
      </Stack>
    </KNDialog>
  )
}

export default CreateOrEditUserDialog
